// The shared metro (docs/plans/2026-10-18-shared-metro.md): a lobby room's players are dropped at different Yellow Line
// stations and catch one train, timed for the whole room, to Patel Chowk. This is the train as a timeline: no physics,
// everything a pure function of the seconds since train 1 left GTB Nagar (its T0), so the server and the client agree.
import { buildField, fieldAt, solidAt } from './map';
import type { GameMap, Vec } from './map';
import { rideStops } from './journey';
import type { JourneyData, JourneyEvent, Routes } from './journey';

export interface Boarding { slug: string; station: string; map: string; names: string[] }
/** data/story/<episode>.metro-mp.json */
export interface MetroMP {
  episode: string; route: string; alight: string; boarding: Boarding[];
  train: { movingS: number; dwellS: number; headwayS: number };
  timetable: { slackS: number; minLeadS: number; maxLeadS: number; gateToPlatformS: number };
  spawn: { walkM: [number, number]; gateRadiusM: number };
  vote: { maxS: number; closeBeforeS: number };
  late: { headwayRealMin: number };
}

/** A stop on the line: `arrS` is when train 1's doors open there, in seconds after its T0. `slug`: a boarding station's. */
export interface Stop { name: string; hi: string; atM: number; arrS: number; slug: string | null }
export interface Line { stops: Stop[]; mps: number; dwellS: number; headwayS: number; lengthM: number; /** doors open at the last stop */ endS: number }

/** The line from the route's first station to the alighting one, timed so the moving part takes `train.movingS`. */
export function makeLine(C: MetroMP, R: Routes): Line {
  const r = R[C.route], last = r.stations!.findIndex((s) => s.name === C.alight);
  if (last < 1) throw new Error(`${C.alight} is not on ${C.route}`);
  const st = r.stations!.slice(0, last + 1), lengthM = st[last].atM, mps = lengthM / C.train.movingS, dwellS = C.train.dwellS;
  const stops = st.map((s, k) => ({ name: s.name, hi: s.hi, atM: s.atM, arrS: s.atM / mps + k * dwellS, slug: C.boarding.find((b) => b.station === s.name)?.slug ?? null }));
  return { stops, mps, dwellS, headwayS: C.train.headwayS, lengthM, endS: stops[last].arrS };
}

export const stopOf = (L: Line, slug: string) => L.stops.findIndex((s) => s.slug === slug);
/** Train n's T0 (n from 1): each runs headwayS behind the one before. */
export const t0Of = (L: Line, t0: number, n: number) => t0 + (n - 1) * L.headwayS;

export interface TrainPos {
  /** metres along the line */
  atM: number;
  /** the stop whose doors are open, -1 while moving (or not yet at the first stop) */
  stop: number;
  /** the next stop it reaches (the last one once done) */
  next: number;
  /** at the last stop: the ride is over */
  done: boolean;
}

/** Where a train is `s` seconds after its own T0. */
export function trainPos(L: Line, s: number): TrainPos {
  const S = L.stops, last = S.length - 1;
  if (s < 0) return { atM: 0, stop: -1, next: 0, done: false };
  for (let k = 0; k <= last; k++) {
    if (s < S[k].arrS) return { atM: S[k - 1].atM + (s - S[k - 1].arrS - L.dwellS) * L.mps, stop: -1, next: k, done: false };
    if (k < last && s < S[k].arrS + L.dwellS) return { atM: S[k].atM, stop: k, next: k + 1, done: false };
  }
  return { atM: S[last].atM, stop: last, next: last, done: true };
}

/**
 * The timetable's one decision, at the whistle: train 1's T0, so that every rider who walks straight to their gate is on
 * the platform `slackS` before the doors open at their stop, within [minLeadS, maxLeadS] from now.
 * `riders`: each player's stop index and their ETA to the platform, in seconds.
 */
export function pickT0(C: MetroMP, L: Line, now: number, riders: { stop: number; etaS: number }[]): number {
  const T = C.timetable;
  const need = Math.max(-Infinity, ...riders.map((r) => r.etaS + T.slackS - L.stops[r.stop].arrS));
  return now + Math.min(T.maxLeadS, Math.max(T.minLeadS, need));
}

/** The train (from 1) a player on the platform of `stop` since `at` catches: the first whose doors there close after `at`. */
export function trainFor(L: Line, t0: number, stop: number, at: number): number {
  const close = t0 + L.stops[stop].arrS + L.dwellS;
  return at <= close ? 1 : 1 + Math.ceil((at - close) / L.headwayS);
}

// ------------------------------------------------------------------ votes

export interface VoteWin { event: JourneyEvent; opensS: number; closesS: number }

/**
 * When each of the ride's events is voted on, in seconds after a train's T0. A station event opens when the doors open
 * there; a between event when the train leaves the first of its two stations; any other when the train passes its
 * point. It closes after vote.maxS, or vote.closeBeforeS before the next stop, whichever comes first.
 */
export function voteWindows(J: JourneyData, R: Routes, C: MetroMP, L: Line): VoteWin[] {
  const idx = (n: string) => L.stops.findIndex((s) => s.name === n);
  const out: VoteWin[] = [];
  for (const { event: e, atM } of rideStops(J, R, C.route)) {
    if (atM > L.lengthM) continue;
    let opensS: number;
    if (e.at.station) { const k = idx(e.at.station); if (k < 0) continue; opensS = L.stops[k].arrS; }
    else if (e.at.between) { const k = idx(e.at.between[0]); if (k < 0 || idx(e.at.between[1]) < 0) continue; opensS = L.stops[k].arrS + L.dwellS; }
    else opensS = timeAtM(L, atM);
    const next = L.stops.find((s) => s.arrS > opensS);
    const closesS = Math.min(opensS + C.vote.maxS, next ? next.arrS - C.vote.closeBeforeS : Infinity);
    if (closesS > opensS) out.push({ event: e, opensS, closesS });
  }
  return out;
}

/** Seconds after T0 that a train passes `m` metres along: moving time plus the dwells at the stops before it. */
export const timeAtM = (L: Line, m: number) => m / L.mps + L.dwellS * L.stops.filter((s) => s.atM < m).length;

/** A vote's result: the majority's choice; a tie, or no votes, takes the event's default (else its first choice). */
export function tally(e: JourneyEvent, votes: Iterable<string>): string {
  const n = new Map<string, number>();
  for (const v of votes) if (e.choices.some((c) => c.id === v)) n.set(v, (n.get(v) ?? 0) + 1);
  const top = Math.max(0, ...n.values()), lead = [...n].filter(([, k]) => k === top).map(([c]) => c);
  return lead.length === 1 ? lead[0] : e.default ?? e.choices[0].id;
}

export interface TrainOutcome {
  /** the arrivals key its riders step out at */
  arrive: string;
  /** the stop they get off at (the last, unless a choice got them off early) */
  offAt: number;
  /** bodies who came along on the way (the students) */
  companions: number;
  /** real minutes the choices add (getting off early and walking) */
  extraLateMin: number;
}

/** What a train's vote results do to its riders: where they get off, where they arrive, who came along. */
export function outcome(J: JourneyData, R: Routes, C: MetroMP, L: Line, results: Record<string, string>): TrainOutcome {
  const o: TrainOutcome = { arrive: 'jm:patel-gate-3', offAt: L.stops.length - 1, companions: 0, extraLateMin: 0 };
  for (const e of J.events) {
    const ch = e.choices.find((c) => c.id === results[e.id]);
    if (!ch) continue;
    o.companions += ch.effect.companions ?? 0;
    if (ch.effect.arrive) {
      o.arrive = ch.effect.arrive;
      const off = e.at.between ? e.at.between[1] : e.at.station;
      const k = L.stops.findIndex((s) => s.name === off);
      if (k >= 0) o.offAt = k;
      // "walk:rajiv-north-edge at walkMPerMin": the walk, less the ride it saves
      const walk = typeof ch.effect.lateMin === 'string' ? R[ch.effect.lateMin.split(' ')[0]] : undefined;
      if (walk) o.extraLateMin += walk.lengthM / J.model.walkMPerMin - realMin(J, L.lengthM - L.stops[o.offAt].atM);
    }
  }
  return o;
}

/** Real minutes for a stretch of the ride, from the journey model's metro speed (stops included). */
export const realMin = (J: JourneyData, m: number) => (m / 1000 / J.model.metro.rideKmh) * 60;
/** Real minutes per game second on the ride: the HUD clock's rate. */
export const realPerGameS = (J: JourneyData, L: Line) => realMin(J, L.lengthM) / L.endS;
/** A train's riders arrive this many real minutes late: one headway a train after the first, plus what their votes add. */
export const lateMinOf = (C: MetroMP, n: number, extra = 0) => (n - 1) * C.late.headwayRealMin + extra;

// ------------------------------------------------------------------ stations and spawns

/** A random boarding station for the next joiner: one nobody in the room has yet, while any is left. */
export function pickStation(slugs: string[], taken: string[], rnd: () => number): string {
  const free = slugs.filter((s) => !taken.includes(s)), from = free.length ? free : slugs;
  return from[Math.floor(rnd() * from.length)];
}

/**
 * The station's own entrances on its map: subway entrances whose name has one of the station's names, each moved to the
 * nearest walkable tile within 3 tiles (smoothing can put an entrance inside a block), and dropped if there is none.
 * World px.
 */
export function stationGates(m: GameMap, names: string[]): Vec[] {
  const ns = names.map((n) => n.toLowerCase()), out: Vec[] = [];
  for (const p of m.pois) {
    if (p.g !== 'metro' || !/subway_entrance/.test(p.tag) || !ns.some((n) => (p.name ?? '').toLowerCase().includes(n))) continue;
    const tx = Math.floor(p.x / m.tile), ty = Math.floor(p.y / m.tile);
    let best: Vec | null = null, bd = Infinity;
    for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
      const x = tx + dx, y = ty + dy;
      if (x < 0 || y < 0 || x >= m.w || y >= m.h || solidAt(m, x, y)) continue;
      const c = { x: (x + 0.5) * m.tile, y: (y + 0.5) * m.tile }, d = Math.hypot(c.x - p.x, c.y - p.y);
      if (dx === 0 && dy === 0) { best = { x: p.x, y: p.y }; bd = -1; } // already walkable: keep it exactly where it is
      else if (d < bd) { best = c; bd = d; }
    }
    if (best) out.push(best);
  }
  return out;
}

/** The gate a player at p should walk to: the nearest by walking, not in a straight line. null if none can be reached. */
export function nearestGate(m: GameMap, gates: Vec[], p: Vec): Vec | null {
  let best: Vec | null = null, bd = Infinity;
  gates.forEach((g, i) => {
    const key = `stn-gate:${i}:${Math.round(g.x)},${Math.round(g.y)}`;
    let F = m.fields.get(key);
    if (!F) { F = buildField(m, [g]); m.fields.set(key, F); }
    const d = fieldAt(m, F, p.x, p.y);
    if (d >= 0 && d < bd) { bd = d; best = g; }
  });
  return best;
}

const METRE = (m: GameMap) => (m.mpt ? m.tile / m.mpt : 1);
/** Walking-path distance to the nearest gate, in tiles (4-connected, so a little long on diagonals), cached on the map. */
export const gateField = (m: GameMap, gates: Vec[]) => {
  let F = m.fields.get('stn-gates');
  if (!F) { F = buildField(m, gates); m.fields.set('stn-gates', F); }
  return F;
};

/** Metres to walk from p to the nearest gate; Infinity where no gate can be reached. */
export function walkToGateM(m: GameMap, gates: Vec[], p: Vec): number {
  const d = fieldAt(m, gateField(m, gates), p.x, p.y);
  return d < 0 ? Infinity : (d * m.tile) / METRE(m);
}

/** Seconds from p to the platform at walking speed `mps`: the walk to the nearest gate, then down the stairs. */
export const etaS = (C: MetroMP, m: GameMap, gates: Vec[], p: Vec, mps: number) => walkToGateM(m, gates, p) / mps + C.timetable.gateToPlatformS;

/**
 * At one of the station's gates: on its tile, or within gateRadiusM of it. The tile counts because walking (and the
 * path hint) ends anywhere on the target tile, which is wider than the radius.
 */
export const atGate = (C: MetroMP, m: GameMap, gates: Vec[], p: Vec) => {
  const tx = Math.floor(p.x / m.tile), ty = Math.floor(p.y / m.tile);
  return gates.some((g) => (Math.floor(g.x / m.tile) === tx && Math.floor(g.y / m.tile) === ty) || Math.hypot(g.x - p.x, g.y - p.y) <= C.spawn.gateRadiusM * METRE(m));
};

/**
 * Where to drop a player: a walkable tile centre whose walk to the nearest gate is within spawn.walkM, at random. If
 * the map has none, the reachable tile farthest from the gates below the upper bound.
 */
export function spawnPoint(C: MetroMP, m: GameMap, gates: Vec[], rnd: () => number): Vec {
  const F = gateField(m, gates), mt = m.tile / METRE(m), [lo, hi] = C.spawn.walkM;
  const ring: number[] = [];
  let best = -1, bestD = -1;
  for (let i = 0; i < F.length; i++) {
    const d = F[i] * mt;
    if (F[i] < 0 || solidAt(m, i % m.w, (i / m.w) | 0)) continue;
    if (d >= lo && d <= hi) ring.push(i);
    else if (d < lo && d > bestD) { best = i; bestD = d; }
  }
  const i = ring.length ? ring[Math.floor(rnd() * ring.length)] : best;
  if (i < 0) return { ...gates[0] };
  return { x: ((i % m.w) + 0.5) * m.tile, y: (((i / m.w) | 0) + 0.5) * m.tile };
}
